import { useState } from "react";
import { Panel, Chip } from "@/components/lux";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyField, MiniToggle, Line, Divider } from "./fields";
import { ShieldCheck, Plus, X } from "lucide-react";
import { money } from "@/dealership/lib/desk";

export default function ProductsPanel({ ws, set, totals, settings, internal }) {
  const [picking, setPicking] = useState(false);
  const products = ws.products || [];
  const update = (i, patch) => set({ products: products.map((p, k) => (k === i ? { ...p, ...patch } : p)) });
  const remove = (i) => set({ products: products.filter((_, k) => k !== i) });
  const addFromCatalog = (cat) => {
    if (products.some((p) => p.id === cat.id)) return;
    set({ products: [...products, { ...cat, enabled: true }] });
  };
  const addCustom = () => set({
    products: [...products, {
      id: `custom_${Date.now()}`, category: "Custom", name: "Custom product", provider: "", tier: "",
      term_months: 0, term_km: 0, price: 0, cost: 0, taxable: true, financed: true, enabled: true,
    }],
  });

  const catalog = settings?.products || [];
  const categories = [...new Set(catalog.map((p) => p.category))];

  return (
    <Panel
      label="F&I products" title="Back-end menu" icon={ShieldCheck} testid="desk-products-panel"
      actions={
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setPicking((v) => !v)} data-testid="desk-open-catalog">
            <Plus className="h-3.5 w-3.5 mr-1" />From catalog
          </Button>
          <Button size="sm" variant="ghost" onClick={addCustom} data-testid="desk-add-custom-product">Custom</Button>
        </div>
      }
    >
      {picking && (
        <div className="mb-4 rounded-2xl border border-border bg-white/[0.02] p-4 space-y-4" data-testid="desk-catalog">
          {categories.map((c) => (
            <div key={c}>
              <div className="label-mono mb-2">{c}</div>
              <div className="flex gap-2 flex-wrap">
                {catalog.filter((p) => p.category === c).map((p) => (
                  <Chip key={p.id} active={products.some((x) => x.id === p.id)} onClick={() => addFromCatalog(p)} testid={`desk-catalog-${p.id}`}>
                    {p.name} · {money(p.price)}
                  </Chip>
                ))}
              </div>
            </div>
          ))}
          {catalog.length === 0 && <p className="text-sm text-muted-foreground">No products in the catalog yet — add them in Desking Settings.</p>}
        </div>
      )}

      <div className="space-y-3">
        {products.length === 0 && <p className="text-sm text-muted-foreground">No products on this deal yet.</p>}
        {products.map((p, i) => (
          <div key={p.id || i} className="rounded-2xl border border-border p-3.5 space-y-2.5" data-testid={`desk-product-${p.id || i}`}>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={p.enabled !== false} onChange={(e) => update(i, { enabled: e.target.checked })} className="accent-[hsl(var(--gold))]" data-testid={`desk-product-enabled-${p.id || i}`} />
              <Input value={p.name} onChange={(e) => update(i, { name: e.target.value })} className="h-9 flex-1" data-testid={`desk-product-name-${p.id || i}`} />
              <button onClick={() => remove(i)} className="p-1.5 rounded-full text-muted-foreground hover:text-red-400" title="Remove" data-testid={`desk-product-remove-${p.id || i}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input value={p.provider || ""} onChange={(e) => update(i, { provider: e.target.value })} placeholder="Provider" className="h-8 w-40 text-xs" data-testid={`desk-product-provider-${p.id || i}`} />
              <Input value={p.tier || ""} onChange={(e) => update(i, { tier: e.target.value })} placeholder="Tier" className="h-8 w-32 text-xs" />
              <Input type="number" value={p.term_months || ""} onChange={(e) => update(i, { term_months: Number(e.target.value) })} placeholder="mo" className="h-8 w-20 text-xs text-right tnum" />
              <Input type="number" value={p.term_km || ""} onChange={(e) => update(i, { term_km: Number(e.target.value) })} placeholder="km" className="h-8 w-24 text-xs text-right tnum" />
              <MiniToggle on={p.taxable !== false} onClick={() => update(i, { taxable: p.taxable === false })} onLabel="Taxable" offLabel="No tax" testid={`desk-product-taxable-${p.id || i}`} />
              <MiniToggle on={p.financed !== false} onClick={() => update(i, { financed: p.financed === false })} onLabel="Financed" offLabel="Pay upfront" testid={`desk-product-financed-${p.id || i}`} />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="label-mono mb-1">Retail</div>
                <MoneyField value={p.price} onChange={(v) => update(i, { price: v })} testid={`desk-product-price-${p.id || i}`} />
              </div>
              {internal && (
                <>
                  <div className="flex-1">
                    <div className="label-mono mb-1">Our cost</div>
                    <MoneyField value={p.cost} onChange={(v) => update(i, { cost: v })} testid={`desk-product-cost-${p.id || i}`} />
                  </div>
                  <div className="flex-1">
                    <div className="label-mono mb-1">Profit</div>
                    <div className="h-9 flex items-center justify-end money tnum text-sm" data-testid={`desk-product-profit-${p.id || i}`}>
                      {money((Number(p.price) || 0) - (Number(p.cost) || 0))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

        <Divider />
        <Line label="Products total" value={totals.products_total} bold gold testid="desk-products-total" />
        {totals.products_upfront > 0 && <Line label="Paid upfront (not financed)" value={totals.products_upfront} muted testid="desk-products-upfront" />}
        {internal && (
          <>
            <Line label="Product cost" value={totals.products_cost} muted />
            <div className="flex items-center gap-3 pt-1">
              <div className="flex-1 label-mono">Finance reserve</div>
              <div className="w-28"><MoneyField value={ws.finance?.finance_reserve} onChange={(v) => set({ finance: { ...ws.finance, finance_reserve: v } })} testid="desk-reserve" /></div>
            </div>
            <Line label="Back-end gross" value={totals.back_gross} bold gold testid="desk-back-gross" />
          </>
        )}
      </div>
    </Panel>
  );
}
